/*
 * Snapping a point to the ways near it: for each edge a test accepts, the
 * nearest point on it, within a radius, nearest first. Distances are metres
 * on a local flat projection, which is exact enough within a few kilometres.
 */
const M_PER_DEG = 111_320;

/** Metres between two points (equirectangular: short distances only). */
export function flatMetres(lat1, lng1, lat2, lng2) {
  const k = Math.cos((((lat1 + lat2) / 2) * Math.PI) / 180);
  const dy = (lat2 - lat1) * M_PER_DEG;
  const dx = (lng2 - lng1) * M_PER_DEG * k;
  return Math.hypot(dx, dy);
}

function project(lat, lng, [la1, lo1], [la2, lo2]) {
  const k = Math.cos((lat * Math.PI) / 180);
  const ax = (lo1 - lng) * k;
  const ay = la1 - lat;
  const bx = (lo2 - lng) * k;
  const by = la2 - lat;
  const dx = bx - ax;
  const dy = by - ay;
  const seg = dx * dx + dy * dy;
  const t = seg === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / seg));
  const pLat = la1 + (la2 - la1) * t;
  const pLng = lo1 + (lo2 - lo1) * t;
  return { t, lat: pLat, lng: pLng, dist: flatMetres(lat, lng, pLat, pLng) };
}

/**
 * Up to `k` candidates, one per edge, within `radiusM`, nearest first:
 * {edge, seg, t, lat, lng, distM, alongM} where alongM is how far along the
 * edge from node a the point lies, in the edge's own (unsimplified) metres.
 */
export function snap(graph, lat, lng, { radiusM, accept = () => true, k = 6 }) {
  graph.build();
  const best = new Map();
  const near = graph.near(lat, lng, radiusM);
  for (let i = 0; i < near.length; i += 2) {
    const edge = near[i];
    const seg = near[i + 1];
    if (!accept(graph.edges[edge], edge)) continue;
    const pts = graph.polyline(edge);
    const p = project(lat, lng, pts[seg], pts[seg + 1]);
    if (p.dist > radiusM) continue;
    const prior = best.get(edge);
    if (!prior || p.dist < prior.distM) best.set(edge, { edge, seg, t: p.t, lat: p.lat, lng: p.lng, distM: p.dist });
  }
  const out = [...best.values()].sort((x, y) => x.distM - y.distM || x.edge - y.edge).slice(0, k);
  for (const c of out) c.alongM = alongMetres(graph, c);
  return out;
}

/** How far along its edge, from node a, a candidate lies (scaled to the edge's length). */
export function alongMetres(graph, c) {
  const pts = graph.polyline(c.edge);
  let before = 0;
  let total = 0;
  for (let s = 0; s + 1 < pts.length; s += 1) {
    const d = flatMetres(pts[s][0], pts[s][1], pts[s + 1][0], pts[s + 1][1]);
    if (s < c.seg) before += d;
    if (s === c.seg) before += d * c.t;
    total += d;
  }
  const len = graph.edges[c.edge].len;
  return total > 0 ? (before / total) * len : 0;
}

/** The part of an edge's polyline from a candidate point to node a ('a') or node b ('b'). */
export function partial(graph, c, toward) {
  const pts = graph.polyline(c.edge);
  const here = [c.lat, c.lng];
  if (toward === 'a') return [here, ...pts.slice(0, c.seg + 1).reverse()];
  return [here, ...pts.slice(c.seg + 1)];
}
